import { useEffect, useState } from "react";
import { Button, Card, Select, Space, Tag, Typography } from "antd";
import { SafetyCertificateOutlined, SaveOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../variables/api/axiosInstance";

const { Text, Title } = Typography;
const PRIMARY = "#242166";

/**
 * « Recours exercé contre qui ? » (1..*) — sélection des compagnies du Pool contre qui le recours
 * est exercé sur le dossier. Composant AUTONOME : il charge lui-même la liste des compagnies
 * (organismes ASSURANCE) et enregistre via PUT /api/sinistre/{codeId}/compagnies-recours.
 * Le champ stocké est la liste des CODES d'organisme ; c'est aussi le filtre du « Listing des recours ».
 */
const RecoursCompagnies = ({ sinistre }) => {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        axiosInstance
            .get("/api/organisme/assurance")
            .then((res) => {
                if (!active) return;
                const opts = (res?.data || [])
                    .filter((o) => o?.code)
                    .map((o) => ({ value: o.code, label: o.raisonSociale || o.code }));
                setOptions(opts);
            })
            .catch(() => {
                if (active) toast.error("Erreur de chargement des compagnies");
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    // Initialise la sélection à partir du dossier (codes déjà enregistrés).
    useEffect(() => {
        setSelected(Array.isArray(sinistre?.compagniesRecours) ? sinistre.compagniesRecours : []);
    }, [sinistre?.codeId, sinistre?.compagniesRecours]);

    const handleSave = () => {
        if (saving || !sinistre?.codeId) return;
        setSaving(true);
        axiosInstance
            .put(`/api/sinistre/${sinistre.codeId}/compagnies-recours`, selected)
            .then(() => toast.success("« Recours contre » enregistré"))
            .catch(() => toast.error("Erreur lors de l'enregistrement"))
            .finally(() => setSaving(false));
    };

    return (
        <Card
            size="small"
            style={{ marginBottom: 16, borderColor: "#e5e7eb" }}
            title={
                <Space>
                    <SafetyCertificateOutlined style={{ color: PRIMARY }} />
                    <span style={{ color: PRIMARY, fontWeight: 600 }}>Recours exercé contre</span>
                </Space>
            }
        >
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                Compagnie(s) du Pool contre qui le recours est exercé sur ce dossier.
            </Text>
            <Space.Compact style={{ width: "100%" }}>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Sélectionner la ou les compagnies…"
                    style={{ width: "100%" }}
                    options={options}
                    value={selected}
                    onChange={setSelected}
                    loading={loading}
                    maxTagCount="responsive"
                />
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    disabled={saving}
                    onClick={handleSave}
                    style={{ background: PRIMARY }}
                >
                    {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
            </Space.Compact>
            {selected?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    {selected.map((code) => {
                        const opt = options.find((o) => o.value === code);
                        return (
                            <Tag key={code} color="blue" style={{ marginBottom: 4 }}>
                                {opt?.label || code}
                            </Tag>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

export default RecoursCompagnies;
